import { Injectable, Optional } from "@angular/core";
import { WebApiConfig, ApiError } from "./web-api.models";
import { Observable, Subject, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { HttpParams } from "@angular/common/http";
import { environment } from "environments/environment";
@Injectable()
export class WebApiService {
	// Private Variables
	private errorSubject = new Subject<ApiError>();
	private config: WebApiConfig = <WebApiConfig>{};
	private progressEvent = new Subject<number>();
	// Public Variables
	public progressEvent$ = this.progressEvent.asObservable();
	public webApiError = this.errorSubject.asObservable();
	public reportProgress?: boolean;
	public responseType?: "json";
	public withCredentials?: boolean;

	// * ----------------------------------------------------------------
	// * @ Constructor
	// * ----------------------------------------------------------------

	constructor(private http: HttpClient, @Optional() config: WebApiConfig) {
		if (config) {
			this.config = config;
		}
	}

	// * ----------------------------------------------------------------
	// * @ private methods
	// * ----------------------------------------------------------------

	private postUrlEncoded<T>(api, body, options, useBaseUrl = true): any {
		const payload = this.transformPayloadToFormUrlEncoded(body).toString();
		// tslint:disable-next-line: no-string-literal
		options["headers"] = {
			"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
		};
		return this.http
			.post<T>(this.fullUrl(false, api, useBaseUrl), payload, options)
			.pipe(this.catchError());
	}

	// private fullUrl(api, useBaseUrl): string {
	// 	return this.config.baseUrl && useBaseUrl
	// 		? this.config.baseUrl + api
	// 		: api;
	// }
	private fullUrl(isMule: boolean, api: string, useBaseUrl = true): string {
		let endpoint = isMule
			? environment.muleSoftApiHost
			: environment.apiEndPoint;
		if (api.includes(endpoint)) {
			return api;
		} else {
			return useBaseUrl
				? (isMule
						? environment.muleSoftApiHost
						: environment.apiEndPoint) + api
				: api;
		}
	}

	private returnErrorObservable(errorResponse, request) {
		return throwError(errorResponse || "server error.");
	}

	private getMuleSoftFullUrl(url: string = ""): string {
		console.log("MULE", environment.muleSoftApiHost + url);
		return environment.muleSoftApiHost + url;
	}

	private getSelfServeFullUrl(url: string = ""): string {
		return environment.selfServeApiHost + url;
	}

	private transformPayloadToFormUrlEncoded(
		payload: any,
		parentKey = null
	): HttpParams {
		const urlEncodedPayload = new HttpParams();
		Object.keys(payload).forEach((property) => {
			let val = payload[property];
			let key = property;
			if (!val) {
				val = "";
			}
			if (parentKey) {
				key = `${parentKey}[${key}]`;
			}
			if (typeof val !== "object") {
				urlEncodedPayload.set(key, val);
			} else {
				const params = this.transformPayloadToFormUrlEncoded(val, key);
				params
					.keys()
					.forEach((v, k) =>
						urlEncodedPayload.set(k.toString(), v[0])
					);
			}
		});
		return urlEncodedPayload;
	}

	private catchError(): any {
		return catchError((errorResponse: any, caught: Observable<any>) => {
			return this.errorHandler(
				errorResponse?.error ? errorResponse?.error : errorResponse
			);
		});
	}

	// * ----------------------------------------------------------------
	// * @ public methods
	// * ----------------------------------------------------------------

	public get<T>(
		api: string,
		useBaseUrl = true,
		extraHeaders?: { [key: string]: any },
		isMule?: boolean
	): Observable<T> {
		let httpOptions: any;
		if (extraHeaders) {
			httpOptions = new HttpHeaders(extraHeaders)
		}
		if (isMule) {
			const muleExtraHeaders = new HttpHeaders({
				groupId: "dodo",
				client_id: environment.clientId,
				client_secret: environment.clientSecret,
			});
			if (!extraHeaders) {
				httpOptions = {
					headers: muleExtraHeaders,
				};
			} else {
				httpOptions = {
					headers: {
						...httpOptions.headers,
						...muleExtraHeaders,
					},
				};
			}
		}
		return this.http
			.get<T>(this.fullUrl(isMule, api, useBaseUrl), httpOptions)
			.pipe(this.catchError());
	}

	public post<T>(
		api,
		body,
		useBaseUrl = true,
		responseType?: string,
		contentType?: string,
		isMule?: boolean
	): Observable<T> {
		let options = {};
		let httpOptions: { headers: any };
		if (responseType) {
			options = { responseType };
		}
		if (isMule) {
			httpOptions = {
				headers: new HttpHeaders({
					groupId: "dodo",
					client_id: environment.clientId,
					client_secret: environment.clientSecret,
				}),
			};
		}
		return contentType === "form-urlencoded"
			? this.postUrlEncoded<T>(api, body, options, useBaseUrl)
			: this.http
					.post<T>(
						this.fullUrl(isMule, api, useBaseUrl),
						body,
						httpOptions
					)
					.pipe(this.catchError());
	}
	public update<T>(
		api,
		item,
		useBaseUrl = true,
		isMule?: boolean
	): Observable<T> {
		return this.http
			.put<T>(this.fullUrl(isMule, api, useBaseUrl), item)
			.pipe(this.catchError());
	}

	public delete<T>(
		api,
		item,
		useBaseUrl = true,
		isMule?: boolean
	): Observable<T> {
		return this.http
			.delete<T>(this.fullUrl(isMule, api, useBaseUrl))
			.pipe(this.catchError());
	}

	public getMS<T>(
		api: string,
		extraHeaders?: { [key: string]: any }
	): Observable<T> {
		console.log("environment settings:", environment);
		let httpOptions: { headers: any };
		if (extraHeaders) {
			const headers = new HttpHeaders(extraHeaders);
			httpOptions = {
				headers: headers,
			};
		}
		return this.http.get<T>(this.getMuleSoftFullUrl(api), httpOptions).pipe(
			catchError((errorResponse) =>
				this.returnErrorObservable(errorResponse, {
					api: this.getMuleSoftFullUrl(api),
					httpOptions,
				})
			)
		);
	}

	/**
	 * GET against the exp-selfserve MuleSoft experience API.
	 *
	 * Uses the self-serve client id/secret and sends the mydodo Auth0 access
	 * token as `Authorization: Bearer <token>`, mirroring mydodo's own
	 * TokenInterceptor. The token cannot be read from a cross-domain cookie
	 * or mydodo's localStorage — mydodo must hand it to this app explicitly
	 * (e.g. via the launch URL), and the caller passes it in here.
	 */
	public getSelfServe<T>(
		api: string,
		authToken: string | null,
		extraHeaders?: { [key: string]: any }
	): Observable<T> {
		const headers = new HttpHeaders({
			groupId: "dodo",
			client_id: environment.selfServeClientId,
			client_secret: environment.selfServeClientSecret,
			...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
			...(extraHeaders || {}),
		});
		const httpOptions = { headers };
		return this.http
			.get<T>(this.getSelfServeFullUrl(api), httpOptions)
			.pipe(
				catchError((errorResponse) =>
					this.returnErrorObservable(errorResponse, {
						api: this.getSelfServeFullUrl(api),
						httpOptions,
					})
				)
			);
	}

	public postMS<T>(
		api: string,
		body: any,
		options?: {
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			observe?: "body";
			params?:
				| HttpParams
				| {
						[param: string]: string | string[];
				  };
			reportProgress?: boolean;
			responseType?: "json";
			withCredentials?: boolean;
		}
	): Observable<T> {
		return this.http
			.post<T>(this.getMuleSoftFullUrl(api), body, options)
			.pipe(
				catchError(
					(errorResponse: ApiError, caught: Observable<any>) => {
						return this.errorHandler(errorResponse);
					}
				)
			);
	}

	public patchMS<T>(
		api: string,
		body: any,
		options?: {
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			observe?: "body";
			params?:
				| HttpParams
				| {
						[param: string]: string | string[];
				  };
			reportProgress?: boolean;
			responseType?: "json";
			withCredentials?: boolean;
		}
	): Observable<T> {
		return this.http
			.patch<T>(this.getMuleSoftFullUrl(api), body, options)
			.pipe(
				catchError(
					(errorResponse: ApiError, caught: Observable<any>) => {
						return this.errorHandler(errorResponse);
					}
				)
			);
	}

	public putMS<T>(
		api: string,
		body: any,
		options?: {
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			observe?: "body";
			params?:
				| HttpParams
				| {
						[param: string]: string | string[];
				  };
			reportProgress?: boolean;
			responseType?: "json";
			withCredentials?: boolean;
		}
	): Observable<T> {
		return this.http
			.put<T>(this.getMuleSoftFullUrl(api), body, options)
			.pipe(
				catchError(
					(errorResponse: ApiError, caught: Observable<any>) => {
						return this.errorHandler(errorResponse);
					}
				)
			);
	}

	public getMuleHealth<T>(
		api: string,
		extraHeaders?: { [key: string]: any }
	): Observable<T> {
		console.log("environment settings mule: ", environment);
		// let httpOptions: { headers: any };
		return this.http.get<T>(this.getMuleSoftFullUrl(api)).pipe(
			catchError((errorResponse) =>
				this.returnErrorObservable(errorResponse, {
					api: this.getMuleSoftFullUrl(api),
					// httpOptions
				})
			)
		);
	}
	public getMuleGetPlan<T>(
		api: string,
		extraHeaders?: { [key: string]: any }
	): Observable<T> {
		console.log("environment settings mule: ", environment);
		// let httpOptions: { headers: any };
		return this.http.get<T>(this.getMuleSoftFullUrl(api)).pipe(
			catchError((errorResponse) =>
				this.returnErrorObservable(errorResponse, {
					api: this.getMuleSoftFullUrl(api),
					// httpOptions
				})
			)
		);
	}

	public patch<T>(
		api: string,
		body: any,
		options?: any,
		isMule?: boolean
	): Observable<T> {
		return this.http
			.patch<T>(this.fullUrl(isMule, api), body, options)
			.pipe(this.catchError());
	}

	public put<T>(
		api: string,
		body: any,
		options?: any,
		isMule?: boolean
	): Observable<T> {
		return this.http
			.put<T>(this.fullUrl(isMule, api), body, options)
			.pipe(this.catchError());
	}

	public errorHandler(apiError: any): any {
		if (apiError.status === "500") {
			// this.router.navigateByUrl('error?ErrorCode=DSF_BROADBAND_SQ_01');
			// Log error where ever you want...
		}
		return throwError(apiError);
	}
}
